import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Spinner } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import Icon from '../../common/Icons';
import MemberService from '../../../services/memberService';
import { downloadDnsTemplate } from './downloadDnsTemplate';
import { parseDNSFile, computeDNSStats } from './parseDNSFile';
import './ImportDNSView.css';

const ITEMS_PER_PAGE = 20;

const PREVIEW_FILTERS = [
  { id: 'all', label: 'Toutes les lignes' },
  { id: 'issues', label: 'Problèmes de rattachement' }
];

function getTypeBadgeClass(type) {
  const t = (type || '').toLowerCase();
  if (t === 'agent') return 'dns-import-type--agent';
  if (t === 'conjoint' || t === 'conjointe') return 'dns-import-type--conjoint';
  return 'dns-import-type--enfant';
}

function isMemberUnlinked(member) {
  return (
    member?.type !== 'AGENT' &&
    !member?.agent_matricule &&
    !member?.agent_import_key &&
    !member?.agent_nom
  );
}

function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} o`;
  return `${(bytes / 1024).toFixed(1)} Ko`;
}

/**
 * Assistant import DNS : société → fichier → aperçu → validation.
 */
const ImportDNSView = ({ companies, showToast, onImportSuccess, variant = 'personnel' }) => {
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [companySearchQuery, setCompanySearchQuery] = useState('');
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewPage, setPreviewPage] = useState(1);
  const [previewFilter, setPreviewFilter] = useState('all');
  const [dragActive, setDragActive] = useState(false);
  const [importing, setImporting] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [invalidExpanded, setInvalidExpanded] = useState(true);

  const fileInputRef = useRef(null);
  const companyWrapRef = useRef(null);
  const companySearchRef = useRef(null);
  const previewRef = useRef(null);
  const [companyDropdownPos, setCompanyDropdownPos] = useState(null);

  const rootClass = `dns-import dns-import--${variant}`;

  const updateCompanyDropdownPos = useCallback(() => {
    const el = companySearchRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setCompanyDropdownPos({
      top: rect.bottom + 6,
      left: rect.left,
      width: Math.max(rect.width, 220)
    });
  }, []);

  useEffect(() => {
    if (!showCompanyDropdown) {
      setCompanyDropdownPos(null);
      return undefined;
    }
    updateCompanyDropdownPos();
    window.addEventListener('resize', updateCompanyDropdownPos);
    window.addEventListener('scroll', updateCompanyDropdownPos, true);
    return () => {
      window.removeEventListener('resize', updateCompanyDropdownPos);
      window.removeEventListener('scroll', updateCompanyDropdownPos, true);
    };
  }, [showCompanyDropdown, companySearchQuery, updateCompanyDropdownPos]);

  useEffect(() => {
    const onDocClick = (e) => {
      const inField = companyWrapRef.current?.contains(e.target);
      const inList = e.target.closest?.('.dns-import-company-list');
      if (!inField && !inList) {
        setShowCompanyDropdown(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const handleFileChange = useCallback(
    (selectedFile) => {
      if (!selectedFile) return;

      if (!selectedCompanyId) {
        showToast("Veuillez d'abord sélectionner une société", 'danger');
        return;
      }

      setParsing(true);
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });

          const parsedResult = parseDNSFile(jsonData);
          const { members: parsedMembers, invalidRows } = parsedResult;

          if (parsedMembers.length === 0) {
            showToast('Aucune ligne valide trouvée dans le fichier DNS', 'danger');
            setPreview(null);
            setFile(null);
            return;
          }

          const stats = computeDNSStats(parsedResult);
          setPreview({ members: parsedMembers, stats, invalidRows });
          setFile(selectedFile);
          setPreviewPage(1);
          setPreviewFilter('all');
          setInvalidExpanded(stats.invalidRows > 0 || stats.unlinkedDependents > 0);

          requestAnimationFrame(() => {
            previewRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          });

          showToast(
            `${stats.total} lignes analysées — ${stats.agents} agents, ${stats.conjoints} conjoints, ${stats.enfants} enfants.`,
            stats.unlinkedDependents > 0 ? 'warning' : 'success'
          );
        } catch (err) {
          showToast(`Erreur lors de l'analyse : ${err.message}`, 'danger');
          setPreview(null);
          setFile(null);
          setPreviewPage(1);
        } finally {
          setParsing(false);
        }
      };

      reader.onerror = () => {
        showToast('Erreur lors de la lecture du fichier', 'danger');
        setParsing(false);
      };

      reader.readAsArrayBuffer(selectedFile);
    },
    [selectedCompanyId, showToast]
  );

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!selectedCompanyId) return;
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (!selectedCompanyId) {
      showToast("Veuillez d'abord sélectionner une société", 'danger');
      return;
    }
    if (e.dataTransfer.files?.[0]) {
      handleFileChange(e.dataTransfer.files[0]);
    }
  };

  const resetImport = () => {
    setFile(null);
    setPreview(null);
    setSelectedCompanyId('');
    setCompanySearchQuery('');
    setPreviewPage(1);
    setPreviewFilter('all');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const clearFile = () => {
    setFile(null);
    setPreview(null);
    setPreviewPage(1);
    setPreviewFilter('all');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDownloadTemplate = async () => {
    try {
      await downloadDnsTemplate();
      showToast('Modèle DNS téléchargé.', 'success');
    } catch (err) {
      showToast('Impossible de télécharger le modèle.', 'danger');
    }
  };

  const handleImport = async () => {
    if (!preview || !selectedCompanyId) {
      showToast('Veuillez sélectionner une société et charger un fichier', 'danger');
      return;
    }
    if (preview.stats?.unlinkedDependents > 0) {
      showToast('Corrigez les ayants-droit non rattachés avant import.', 'danger');
      return;
    }

    try {
      setImporting(true);
      const payload = {
        company_id: parseInt(selectedCompanyId, 10),
        members: preview.members.map((m) => ({
          rowNumber: m.rowNumber,
          matricule: m.matricule,
          nom: m.nom,
          prenom: m.prenom,
          fonction: m.fonction || null,
          type: m.type,
          date_naissance: m.date_naissance,
          sexe: m.sexe || null,
          date_embauche: m.date_embauche,
          agent_matricule: m.agent_matricule || null,
          agent_import_key: m.agent_import_key || null
        }))
      };

      const response = await MemberService.importDNS(payload);
      const stats = response.data?.stats || {};
      const created = (stats.agentsCreated || 0) + (stats.dependentsCreated || 0);
      const skipped = (stats.agentsSkipped || 0) + (stats.dependentsSkipped || 0);
      const failedCount = response.data?.failed ?? 0;

      if (created > 0 && failedCount === 0) {
        showToast(
          `Import réussi : ${created} créé(s)${skipped ? `, ${skipped} déjà existant(s)` : ''}.`,
          'success'
        );
      } else if (created > 0) {
        showToast(
          `Import partiel : ${created} créé(s), ${failedCount} erreur(s).`,
          'warning'
        );
      } else {
        showToast(`Aucun enregistrement importé (${failedCount} erreur(s)).`, 'danger');
      }

      setTimeout(() => {
        resetImport();
        if (typeof onImportSuccess === 'function') onImportSuccess();
      }, 1600);
    } catch (err) {
      showToast(err.message || "Erreur lors de l'import", 'danger');
    } finally {
      setImporting(false);
    }
  };

  const filteredCompanies = companies.filter((c) => {
    const q = companySearchQuery.toLowerCase().trim();
    if (!q) return true;
    return (
      c.name?.toLowerCase().includes(q) ||
      c.company_code?.toLowerCase().includes(q)
    );
  });

  const selectedCompany = companies.find((c) => c.id === parseInt(selectedCompanyId, 10));

  const filteredPreviewMembers = useMemo(() => {
    if (!preview?.members) return [];
    if (previewFilter === 'issues') {
      return preview.members.filter(isMemberUnlinked);
    }
    return preview.members;
  }, [preview, previewFilter]);

  const totalPreviewPages = filteredPreviewMembers.length
    ? Math.ceil(filteredPreviewMembers.length / ITEMS_PER_PAGE)
    : 0;
  const previewStart = (previewPage - 1) * ITEMS_PER_PAGE;
  const currentPageMembers = filteredPreviewMembers.slice(
    previewStart,
    previewStart + ITEMS_PER_PAGE
  );

  useEffect(() => {
    setPreviewPage(1);
  }, [previewFilter]);

  const step1Done = Boolean(selectedCompanyId);
  const step2Done = Boolean(file && preview);
  const hasBlockingIssues = (preview?.stats?.unlinkedDependents || 0) > 0;
  const canConfirmImport =
    Boolean(preview?.members?.length) && !hasBlockingIssues && !importing;

  const readyRatio = preview
    ? `${preview.stats.total - (preview.stats.unlinkedDependents || 0)}/${preview.stats.total}`
    : null;

  return (
    <>
      <div className={rootClass}>
        <header className="dns-import-hero">
          <div className="dns-import-hero__main">
            <div className="dns-import-hero__icon" aria-hidden>
              <Icon name="fileExcel" size={24} />
            </div>
            <div>
              <h2 className="dns-import-hero__title">Import fichier DNS</h2>
              <p className="dns-import-hero__desc">
                Chargez le modèle Excel de votre société. Les agents doivent apparaître
                avant leurs ayants-droit dans le fichier.
              </p>
              <ul className="dns-import-hero__chips" aria-label="Informations">
                <li>.xlsx · .xls · .csv</li>
                <li>Aperçu obligatoire</li>
                <li>Rattachement familial vérifié</li>
              </ul>
            </div>
          </div>
          <button
            type="button"
            className="dns-import-hero__template-btn"
            onClick={handleDownloadTemplate}
          >
            <Icon name="download" size={16} />
            Télécharger le modèle
          </button>
        </header>

        <nav className="dns-import-steps" aria-label="Étapes de l'import">
          <div className={`dns-import-step ${step1Done ? 'is-done' : 'is-active'}`}>
            <span className="dns-import-step__badge">{step1Done ? '✓' : '1'}</span>
            <span className="dns-import-step__text">Société</span>
          </div>
          <div
            className={`dns-import-step ${
              step1Done && !step2Done ? 'is-active' : step2Done ? 'is-done' : ''
            }`}
          >
            <span className="dns-import-step__badge">{step2Done ? '✓' : '2'}</span>
            <span className="dns-import-step__text">Fichier</span>
          </div>
          <div className={`dns-import-step ${step2Done ? 'is-active' : ''}`}>
            <span className="dns-import-step__badge">3</span>
            <span className="dns-import-step__text">Validation</span>
          </div>
        </nav>

        <div className={`dns-import-grid ${step2Done ? 'has-preview' : ''}`}>
          <aside className="dns-import-setup">
            <section
              className={`dns-import-card ${step1Done ? 'is-complete' : 'is-current'}`}
              aria-labelledby="dns-step-company"
            >
              <div className="dns-import-card__head">
                <span className="dns-import-card__step">1</span>
                <div>
                  <h3 id="dns-step-company">Société cible</h3>
                  <p>Tous les adhérents seront rattachés à cette société</p>
                </div>
              </div>
              <div className="dns-import-card__body">
                <div className="dns-import-company" ref={companyWrapRef}>
                  <label className="dns-import-field-label" htmlFor="dns-company-search">
                    Rechercher
                  </label>
                  <div className="dns-import-company-field">
                    <div className="dns-import-search" ref={companySearchRef}>
                      <Icon name="search" size={16} aria-hidden />
                      <input
                        id="dns-company-search"
                        type="search"
                        placeholder="Nom ou code société…"
                        value={companySearchQuery}
                        onChange={(e) => {
                          setCompanySearchQuery(e.target.value);
                          setShowCompanyDropdown(true);
                        }}
                        onFocus={() => setShowCompanyDropdown(true)}
                        autoComplete="off"
                        role="combobox"
                        aria-expanded={showCompanyDropdown}
                        aria-controls="dns-company-listbox"
                        aria-autocomplete="list"
                      />
                    </div>
                  </div>
                  {showCompanyDropdown &&
                    companyDropdownPos &&
                    typeof document !== 'undefined' &&
                    createPortal(
                      <ul
                        id="dns-company-listbox"
                        className="dns-import-company-list dns-import-company-list--portal"
                        role="listbox"
                        style={{
                          position: 'fixed',
                          top: companyDropdownPos.top,
                          left: companyDropdownPos.left,
                          width: companyDropdownPos.width,
                          zIndex: 1500
                        }}
                      >
                        {filteredCompanies.length > 0 ? (
                          filteredCompanies.map((company) => (
                            <li key={company.id} role="none">
                              <button
                                type="button"
                                role="option"
                                aria-selected={selectedCompanyId === String(company.id)}
                                className={`dns-import-company-option ${
                                  selectedCompanyId === String(company.id) ? 'is-selected' : ''
                                }`}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => {
                                  setSelectedCompanyId(String(company.id));
                                  setShowCompanyDropdown(false);
                                  setCompanySearchQuery('');
                                  clearFile();
                                }}
                              >
                                <span className="dns-import-company-option__code">
                                  {company.company_code || '—'}
                                </span>
                                <span className="dns-import-company-option__name">
                                  {company.name}
                                </span>
                              </button>
                            </li>
                          ))
                        ) : (
                          <li className="dns-import-company-list__empty">Aucune société trouvée</li>
                        )}
                      </ul>,
                      document.body
                    )}
                </div>
                {selectedCompany && (
                  <div className="dns-import-pill dns-import-pill--success">
                    <Icon name="checkCircle" size={16} />
                    <span>
                      <strong>{selectedCompany.company_code || '—'}</strong> —{' '}
                      {selectedCompany.name}
                    </span>
                  </div>
                )}
              </div>
            </section>

            <section
              className={`dns-import-card ${!step1Done ? 'is-locked' : step2Done ? 'is-complete' : 'is-current'}`}
              aria-labelledby="dns-step-file"
            >
              <div className="dns-import-card__head">
                <span className="dns-import-card__step">2</span>
                <div>
                  <h3 id="dns-step-file">Fichier DNS</h3>
                  <p>
                    {step1Done
                      ? 'Glissez-déposez ou sélectionnez votre fichier'
                      : 'Choisissez d’abord une société'}
                  </p>
                </div>
              </div>
              <div className="dns-import-card__body">
                <div
                  className={`dns-import-dropzone ${dragActive ? 'is-drag' : ''} ${
                    !step1Done ? 'is-disabled' : ''
                  } ${parsing ? 'is-loading' : ''}`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                  {parsing ? (
                    <div className="dns-import-dropzone__loading">
                      <Spinner animation="border" size="sm" variant="primary" />
                      <p>Analyse du fichier en cours…</p>
                    </div>
                  ) : (
                    <>
                      <div className="dns-import-dropzone__icon">
                        <Icon name="cloudUpload" size={32} />
                      </div>
                      <p className="dns-import-dropzone__title">Déposer le fichier ici</p>
                      <p className="dns-import-dropzone__hint">ou parcourir depuis votre poste</p>
                      <input
                        ref={fileInputRef}
                        id="dns-file-input"
                        type="file"
                        className="dns-import-file-input"
                        accept=".xlsx,.xls,.csv"
                        disabled={!step1Done || parsing}
                        onChange={(e) => handleFileChange(e.target.files?.[0])}
                      />
                      <label htmlFor="dns-file-input" className="dns-import-browse">
                        Parcourir les fichiers
                      </label>
                    </>
                  )}
                </div>

                {file && !parsing && (
                  <div className="dns-import-file">
                    <div className="dns-import-file__icon">
                      <Icon name="fileExcel" size={18} />
                    </div>
                    <div className="dns-import-file__meta">
                      <span className="dns-import-file__name">{file.name}</span>
                      <span className="dns-import-file__sub">
                        {formatFileSize(file.size)} ·{' '}
                        {new Date(file.lastModified).toLocaleDateString('fr-FR')}
                      </span>
                    </div>
                    <button type="button" className="dns-import-link-btn" onClick={clearFile}>
                      Changer
                    </button>
                  </div>
                )}
              </div>
            </section>
          </aside>

          <main className="dns-import-preview" ref={previewRef}>
            {!preview ? (
              <div className="dns-import-preview-empty">
                <div className="dns-import-preview-empty__icon">
                  <Icon name="inbox" size={32} />
                </div>
                <h3>Aperçu des données</h3>
                <p>
                  Une fois le fichier analysé, vous verrez ici les statistiques, les alertes
                  et le détail ligne par ligne avant confirmation.
                </p>
              </div>
            ) : (
              <section className="dns-import-card dns-import-card--preview is-current">
                <div className="dns-import-card__head">
                  <span className="dns-import-card__step">3</span>
                  <div>
                    <h3>Vérification & import</h3>
                    <p>Contrôlez les données puis confirmez l’enregistrement</p>
                  </div>
                  {readyRatio && (
                    <span
                      className={`dns-import-ready-badge ${
                        hasBlockingIssues ? 'has-error' : ''
                      }`}
                    >
                      {readyRatio} prêtes
                    </span>
                  )}
                </div>

                <div className="dns-import-card__body">
                  <div className="dns-import-kpis" aria-live="polite">
                    <div className="dns-import-kpi dns-import-kpi--agent">
                      <span className="dns-import-kpi__value">{preview.stats.agents}</span>
                      <span className="dns-import-kpi__label">Agents</span>
                    </div>
                    <div className="dns-import-kpi dns-import-kpi--spouse">
                      <span className="dns-import-kpi__value">{preview.stats.conjoints}</span>
                      <span className="dns-import-kpi__label">Conjoints</span>
                    </div>
                    <div className="dns-import-kpi dns-import-kpi--child">
                      <span className="dns-import-kpi__value">{preview.stats.enfants}</span>
                      <span className="dns-import-kpi__label">Enfants</span>
                    </div>
                    <div className="dns-import-kpi dns-import-kpi--ok">
                      <span className="dns-import-kpi__value">{preview.stats.total}</span>
                      <span className="dns-import-kpi__label">Lignes valides</span>
                    </div>
                    <div className="dns-import-kpi dns-import-kpi--warn">
                      <span className="dns-import-kpi__value">{preview.stats.invalidRows}</span>
                      <span className="dns-import-kpi__label">Lignes rejetées</span>
                    </div>
                  </div>

                  {hasBlockingIssues && (
                    <div className="dns-import-banner dns-import-banner--danger" role="alert">
                      <Icon name="alertTriangle" size={20} />
                      <div>
                        <strong>
                          {preview.stats.unlinkedDependents} ayant-droit(s) non rattaché(s)
                        </strong>
                        <p>
                          Chaque ayant-droit doit suivre son agent dans le fichier. L’import est
                          bloqué tant que ce point n’est pas corrigé.
                        </p>
                      </div>
                    </div>
                  )}

                  {(preview.invalidRows?.length || 0) > 0 && (
                    <div className="dns-import-accordion">
                      <button
                        type="button"
                        className="dns-import-accordion__toggle"
                        aria-expanded={invalidExpanded}
                        onClick={() => setInvalidExpanded((v) => !v)}
                      >
                        <Icon name={invalidExpanded ? 'chevronDown' : 'chevronRight'} size={16} />
                        <span>
                          {preview.invalidRows.length} ligne(s) invalide(s) dans le fichier source
                        </span>
                      </button>
                      {invalidExpanded && (
                        <div className="dns-import-accordion__body">
                          <div className="dns-import-table-scroll">
                            <table className="dns-import-table dns-import-table--errors">
                              <thead>
                                <tr>
                                  <th>Ligne</th>
                                  <th>Matricule</th>
                                  <th>Nom</th>
                                  <th>Prénom</th>
                                  <th>Motif</th>
                                </tr>
                              </thead>
                              <tbody>
                                {preview.invalidRows.slice(0, 12).map((row, idx) => (
                                  <tr key={`${row.rowNumber}-${idx}`}>
                                    <td>{row.rowNumber}</td>
                                    <td>{row.matricule || '—'}</td>
                                    <td>{row.nom || '—'}</td>
                                    <td>{row.prenom || '—'}</td>
                                    <td>{row.reasons?.join(', ')}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          {preview.invalidRows.length > 12 && (
                            <p className="dns-import-accordion__more">
                              + {preview.invalidRows.length - 12} autre(s) ligne(s)
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="dns-import-toolbar">
                    <div className="dns-import-segment" role="group" aria-label="Filtre aperçu">
                      {PREVIEW_FILTERS.map((f) => (
                        <button
                          key={f.id}
                          type="button"
                          aria-pressed={previewFilter === f.id}
                          className={`dns-import-segment__btn ${
                            previewFilter === f.id ? 'is-active' : ''
                          }`}
                          onClick={() => setPreviewFilter(f.id)}
                        >
                          {f.label}
                          {f.id === 'issues' && preview.stats.unlinkedDependents > 0
                            ? ` (${preview.stats.unlinkedDependents})`
                            : ''}
                        </button>
                      ))}
                    </div>
                    <span className="dns-import-toolbar__meta">
                      {filteredPreviewMembers.length} ligne(s) affichée(s)
                    </span>
                  </div>

                  <div className="dns-import-table-scroll">
                    <table className="dns-import-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Matricule</th>
                          <th>Nom</th>
                          <th>Prénom</th>
                          <th>Type</th>
                          <th>Rattachement</th>
                          <th>Naissance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentPageMembers.length === 0 ? (
                          <tr>
                            <td colSpan={7} className="dns-import-table__empty">
                              Aucune ligne pour ce filtre
                            </td>
                          </tr>
                        ) : (
                          currentPageMembers.map((member, idx) => {
                            const unlinked = isMemberUnlinked(member);
                            return (
                              <tr
                                key={`${member.rowNumber}-${idx}`}
                                className={unlinked ? 'is-error' : ''}
                              >
                                <td className="text-muted">{previewStart + idx + 1}</td>
                                <td>
                                  <code className="dns-import-mat">{member.matricule}</code>
                                  {member.matricule_generated && (
                                    <span className="dns-import-tag">auto</span>
                                  )}
                                </td>
                                <td>{member.nom}</td>
                                <td>{member.prenom || '—'}</td>
                                <td>
                                  <span
                                    className={`dns-import-type ${getTypeBadgeClass(member.type)}`}
                                  >
                                    {member.type}
                                  </span>
                                </td>
                                <td>
                                  {member.type === 'AGENT' ? (
                                    <span className="text-muted">—</span>
                                  ) : unlinked ? (
                                    <span className="dns-import-link dns-import-link--bad">
                                      <Icon name="alertTriangle" size={12} />
                                      Non rattaché
                                    </span>
                                  ) : (
                                    <span className="dns-import-link dns-import-link--ok">
                                      <Icon name="check" size={12} />
                                      {[member.agent_nom, member.agent_prenom]
                                        .filter(Boolean)
                                        .join(' ') || member.agent_matricule}
                                    </span>
                                  )}
                                </td>
                                <td>{member.date_naissance || '—'}</td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>

                  {filteredPreviewMembers.length > ITEMS_PER_PAGE && (
                    <footer className="dns-import-pagination">
                      <span>
                        {previewStart + 1}–
                        {Math.min(previewStart + ITEMS_PER_PAGE, filteredPreviewMembers.length)}{' '}
                        sur {filteredPreviewMembers.length}
                      </span>
                      <nav aria-label="Pagination aperçu">
                        <button
                          type="button"
                          disabled={previewPage <= 1}
                          onClick={() => setPreviewPage((p) => p - 1)}
                          aria-label="Page précédente"
                        >
                          <Icon name="chevronLeft" size={14} />
                        </button>
                        <span>
                          {previewPage} / {totalPreviewPages}
                        </span>
                        <button
                          type="button"
                          disabled={previewPage >= totalPreviewPages}
                          onClick={() => setPreviewPage((p) => p + 1)}
                          aria-label="Page suivante"
                        >
                          <Icon name="chevronRight" size={14} />
                        </button>
                      </nav>
                    </footer>
                  )}
                </div>
              </section>
            )}
          </main>
        </div>
      </div>

      {preview && (
        <footer className="dns-import-sticky" role="region" aria-label="Actions d'import">
          <div className="dns-import-sticky__inner">
            <div className="dns-import-sticky__summary">
              <span className="dns-import-sticky__company">
                {selectedCompany?.name || 'Société'}
              </span>
              <span className="dns-import-sticky__detail">
                {preview.stats.total} lignes · {readyRatio} prêtes à l’import
                {hasBlockingIssues ? ' · corrections requises' : ''}
              </span>
            </div>
            <div className="dns-import-sticky__actions">
              <button
                type="button"
                className="dns-import-btn dns-import-btn--ghost"
                onClick={clearFile}
                disabled={importing}
              >
                Annuler
              </button>
              <button
                type="button"
                className="dns-import-btn dns-import-btn--primary"
                onClick={handleImport}
                disabled={!canConfirmImport}
                title={
                  hasBlockingIssues
                    ? 'Corrigez les rattachements avant import'
                    : 'Enregistrer les adhérents'
                }
              >
                <Icon name="check" size={16} />
                {importing ? 'Import…' : 'Confirmer l’import'}
              </button>
            </div>
          </div>
        </footer>
      )}

      {importing && (
        <div
          className="dns-import-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="dns-import-loading-title"
        >
          <div className="dns-import-overlay__card">
            <Spinner animation="border" variant="primary" />
            <h3 id="dns-import-loading-title">Import en cours</h3>
            <p>Enregistrement des adhérents — ne fermez pas cette page.</p>
            <div className="dns-import-overlay__bar" aria-hidden>
              <span />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ImportDNSView;
